import { useEffect, useState, useCallback, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  api,
  Card,
  Button,
  buttonClasses,
  PillButton,
  pillClasses,
  AvatarImage,
  ConfirmDialog,
  useAuth,
  useBrand,
  brandDisplayName,
  apiErrorMessage,
} from '@aralere/shared-ui';
import type { ClientCatalogItem, ClientCatalogPackage, GetClientCatalogResponse, RevealCredentialResponse } from '@aralere/shared-types';

// ──────────────────────────────────────────────────────────────────────────────
// Client-facing service catalog (Stage 2, spec §5.5/§5.6/§5.9; 04 Active Backlog #5 + #14). Parts:
//   • "Your services"  — the active services the client holds. Launchable ones (logo + client-facing
//     name → launch URL, spec §5.6) and status-only ones (an info reveal — nothing to open) sit in
//     their own rows. Package-contained services appear here individually too, so each is launchable.
//   • "Packages"       — the §5.9 bundles. "Your packages": a package is NOT a single launcher (it holds
//     several services), so its card OPENS to reveal the contained services, each launchable. "Packages
//     you can add": other brand packages, added with the same recurring-charge consent as a service.
//   • "Add services"   — the "similar items" view of active catalog services the client can add
//     (§5.5): complementary recommendations first, category-suppressed alternatives hidden (§5.4).
//
// Data + writes are CALLER-OWN-RECORD (api.account.* — no id; the server resolves the caller's own
// client). Items carry the resolved client PRICE only — never cost/margin (§11.2). No billing or
// live provisioning happens here (Stage 4 / #11): a client add records consent + a pending
// assignment, and the success copy sets that expectation.
// ──────────────────────────────────────────────────────────────────────────────

function priceLabel(item: ClientCatalogItem): string {
  if (item.metered) return 'Usage-based';
  if (item.price == null) return '';
  if (item.price <= 0) return 'Included';
  return `$${item.price.toFixed(2)}/mo`;
}

function packagePriceLabel(pkg: ClientCatalogPackage): string {
  // A package always carries one resolved price (priceOverride ?? defaultPrice); never metered (§5.9).
  if (pkg.price <= 0) return 'Included';
  return `$${pkg.price.toFixed(2)}/mo`;
}

// Locked-service treatment (slice 8c). A non-payment suspension shows a Pay affordance that deep-links
// to the specific past-due invoice (one) or the Billing list (several); any other suspension shows the
// contact-us prompt. Managed (linked) accounts always resolve to the contact prompt server-side.
function SuspendedInfo({ reason, payInvoiceIds }: { reason?: 'non-payment' | 'other'; payInvoiceIds?: string[] }): JSX.Element {
  if (reason === 'non-payment') {
    const href = payInvoiceIds && payInvoiceIds.length === 1 ? `/billing?invoice=${payInvoiceIds[0]}` : '/billing';
    return (
      <div className="flex flex-wrap items-center justify-end gap-2">
        <span className="inline-flex items-center gap-1.5 text-xs text-[var(--tint-red-text)]">
          <span className="h-2 w-2 rounded-full bg-red-500" /> Past due, pay to restore
        </span>
        <Link to={href} className={pillClasses('amber', 'lg')}>
          Pay now
        </Link>
      </div>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-[var(--tint-warn-text)]">
      <span className="h-2 w-2 rounded-full bg-amber-500" /> Paused, contact us to resume
    </span>
  );
}

export function ClientServices(): JSX.Element {
  const { brand } = useBrand();
  const { activeClient } = useAuth();
  const brandName = brand ? brandDisplayName(brand.name) : '';
  // supportReplyTo is a Business-only field, not in the public brand projection, so it
  // is unavailable to the SPA today — the support link below stays hidden. (Deferred:
  // add supportReplyTo to PublicBrand + toPublicBrand to surface a real support email.)
  const supportEmail: string | null = null;

  const [data, setData] = useState<GetClientCatalogResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Add-service recurring-consent dialog (§5.5)
  const [addTarget, setAddTarget] = useState<ClientCatalogItem | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [addBusy, setAddBusy] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  // Add-package recurring-consent dialog (§5.5/§5.9) — its own state; only one dialog opens at a time.
  const [addPkgTarget, setAddPkgTarget] = useState<ClientCatalogPackage | null>(null);
  const [pkgAck, setPkgAck] = useState(false);
  const [addPkgBusy, setAddPkgBusy] = useState(false);
  const [addPkgError, setAddPkgError] = useState<string | null>(null);

  // Status-only "details" reveal (which serviceId is expanded), which held package is open to its
  // contained services (§5.9 — open the bundle to reach its launchers), + post-add confirmation.
  const [openInfo, setOpenInfo] = useState<string | null>(null);
  const [openPkg, setOpenPkg] = useState<string | null>(null);
  const [addedName, setAddedName] = useState<string | null>(null);

  // one-time credential reveal (08 Q32, s39) — warn step, then the single show step
  const [revealTarget, setRevealTarget] = useState<ClientCatalogItem | null>(null);
  const [revealBusy, setRevealBusy] = useState(false);
  const [revealError, setRevealError] = useState<string | null>(null);
  const [revealed, setRevealed] = useState<RevealCredentialResponse | null>(null);
  const [attestChecked, setAttestChecked] = useState(false);
  const [copied, setCopied] = useState<'user' | 'pass' | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setLoadError(null);
    try {
      setData(await api.account.getCatalog());
    } catch (e) {
      setLoadError(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const openAdd = (item: ClientCatalogItem): void => {
    setAddTarget(item);
    setAcknowledged(false);
    setAddError(null);
    setAddedName(null);
  };

  const confirmAdd = async (): Promise<void> => {
    if (!addTarget || !acknowledged) return;
    setAddBusy(true);
    setAddError(null);
    try {
      await api.account.subscribeToService(addTarget.serviceId, true);
      const name = addTarget.name;
      setAddTarget(null);
      setAddedName(name);
      await load(); // recompute available — suppression + recommendations shift after an add
    } catch (e) {
      setAddError(apiErrorMessage(e));
    } finally {
      setAddBusy(false);
    }
  };

  const openAddPkg = (pkg: ClientCatalogPackage): void => {
    setAddPkgTarget(pkg);
    setPkgAck(false);
    setAddPkgError(null);
    setAddedName(null);
  };

  const confirmAddPkg = async (): Promise<void> => {
    if (!addPkgTarget || !pkgAck) return;
    setAddPkgBusy(true);
    setAddPkgError(null);
    try {
      await api.account.subscribeToPackage(addPkgTarget.packageId, true);
      const name = addPkgTarget.name;
      setAddPkgTarget(null);
      setAddedName(name);
      await load(); // the new package moves to "Your packages" + its services join "Your services"
    } catch (e) {
      setAddPkgError(apiErrorMessage(e));
    } finally {
      setAddPkgBusy(false);
    }
  };

  // ── one-time credential reveal (08 Q32) ──
  const openReveal = (item: ClientCatalogItem): void => {
    setRevealTarget(item);
    setRevealError(null);
    setAttestChecked(false);
    setCopied(null);
  };
  const doReveal = async (): Promise<void> => {
    if (!revealTarget) return;
    setRevealBusy(true);
    setRevealError(null);
    try {
      const res = await api.account.revealServiceCredential(revealTarget.serviceId);
      setRevealed(res);
      setRevealTarget(null);
      void load(); // clears the badge (the handoff is now consumed)
    } catch (e) {
      // 409 (already viewed) / 410 (expired) return friendly copy from the API; refresh so the
      // badge reflects the real state either way.
      setRevealError(apiErrorMessage(e));
      void load();
    } finally {
      setRevealBusy(false);
    }
  };
  const closeRevealed = (): void => {
    // Optional best-effort attestation, then scrub ALL reveal state (the password must not linger).
    if (revealed && attestChecked) void api.account.attestServiceCredential(revealed.serviceId);
    setRevealed(null);
    setAttestChecked(false);
    setCopied(null);
  };
  const copyCred = (kind: 'user' | 'pass', value: string): void => {
    void navigator.clipboard.writeText(value).then(() => {
      setCopied(kind);
      window.setTimeout(() => setCopied((c) => (c === kind ? null : c)), 1500);
    });
  };

  const active = data?.active ?? [];
  const launchable = active.filter((i) => i.status !== 'suspended' && i.launchUrl);
  const statusOnly = active.filter((i) => i.status !== 'suspended' && !i.launchUrl);
  const paused = active.filter((i) => i.status === 'suspended');
  const available = data?.available ?? [];
  const recommended = available.filter((i) => i.recommended);
  const more = available.filter((i) => !i.recommended);
  const hasActive = launchable.length + statusOnly.length + paused.length > 0;

  const heldPackages = data?.packages ?? [];
  const availablePackages = data?.availablePackages ?? [];
  const hasPackages = heldPackages.length + availablePackages.length > 0;

  // s33 linked-account model: when this consumer account is linked to a business, the session chrome
  // already carries the managing business as activeClient (clientType 'business'). We derive `managed`
  // from that — available on the first paint, so the header never flashes the unmanaged copy while the
  // async /api/account-services call is in flight. Both signals reflect the same server-side link state;
  // account-services still returns empty available/availablePackages and refuses self-subscribe with 403
  // services_managed_by_business, so the server stays the backstop. Held services render read-only (launch
  // still works) under a "managed by your organization" treatment; the business name comes from
  // activeClient, else the generic fallback.
  const managed = activeClient?.clientType === 'business';
  // WS5: a past-due client can browse but can't add until they pay (the server enforces it too).
  const blocked = !!data?.selfServiceBlocked;
  const managerLabel = managed && activeClient?.displayName ? activeClient.displayName : 'your organization';

  return (
    <div className="space-y-10">
      <header>
        <h1 className="font-display text-3xl tracking-tight">Services</h1>
        <p className="text-sm text-[var(--color-muted)]">
          {managed ? (
            `Launch the services ${managerLabel} has set up for your account.`
          ) : (
            <>{brandName ? `Your ${brandName} services` : 'Your services'}. Launch what you have, and add more anytime.</>
          )}
        </p>
      </header>

      {loading ? <p className="text-sm text-[var(--color-muted)]">Loading…</p> : null}

      {loadError ? (
        <Card>
          <p className="text-sm text-[var(--color-muted)]">
            We couldn’t load your services right now. Please refresh, or contact us if it keeps happening.
          </p>
        </Card>
      ) : null}

      {addedName ? (
        <div className="rounded-md bg-[var(--tint-green-bg-soft)] border border-[var(--tint-green-border)] px-4 py-3 text-sm text-[var(--tint-green-text)]">
          <strong>{addedName}</strong> added. We’ll get it set up for you shortly.
        </div>
      ) : null}

      {managed ? (
        <div className="rounded-md border border-[var(--color-accent)]/30 bg-[var(--color-accent)]/10 px-4 py-3 text-sm text-[var(--color-text)]">
          Your services are managed by {managerLabel}. You can launch what you have below. Contact{' '}
          {managerLabel} to add or change a service.
        </div>
      ) : null}

      {data ? (
        <>
          {/* ── Your services ───────────────────────────────────────────── */}
          <section className="space-y-4">
            <h2 className="font-display text-lg">Your services</h2>

            {!hasActive ? (
              <Card>
                <p className="text-sm text-[var(--color-muted)]">
                  {managed
                    ? `${managerLabel} hasn’t assigned any services to your account yet.`
                    : 'You don’t have any services yet. Browse what’s available below to get started.'}
                </p>
              </Card>
            ) : null}

            {launchable.length > 0 ? (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {launchable.map((item) => (
                  <CatalogCard key={item.serviceId} item={item}>
                    {item.credentialReady ? (
                      <div>
                        <PillButton tone="amber" onClick={() => openReveal(item)}>
                          View sign-in details
                        </PillButton>
                        {item.credentialExpiresAt ? (
                          <p className="mt-1 text-[11px] text-[var(--color-muted)]">
                            One-time reveal, expires {item.credentialExpiresAt.slice(0, 10)}
                          </p>
                        ) : null}
                      </div>
                    ) : null}
                    <a
                      href={item.launchUrl ?? '#'}
                      target="_blank"
                      rel="noreferrer noopener"
                      onClick={() => void api.account.logServiceOpen(item.serviceId)}
                      className={buttonClasses('primary', 'sm')}
                    >
                      Open ↗
                    </a>
                    {item.manualAccessMethod === 'instructions' && item.accessInstructions ? (
                      <div className="mt-1.5">
                        <button
                          type="button"
                          onClick={() => setOpenInfo(openInfo === `signin-${item.serviceId}` ? null : `signin-${item.serviceId}`)}
                          className="block text-xs text-[var(--color-muted)] underline hover:no-underline"
                        >
                          {openInfo === `signin-${item.serviceId}` ? 'Hide sign-in help' : 'How to sign in'}
                        </button>
                        {openInfo === `signin-${item.serviceId}` ? (
                          <p className="mt-1 text-xs text-[var(--color-muted)] whitespace-pre-wrap">{item.accessInstructions}</p>
                        ) : null}
                      </div>
                    ) : null}
                  </CatalogCard>
                ))}
              </div>
            ) : null}

            {statusOnly.length > 0 ? (
              <div className="space-y-3">
                <p className="text-xs uppercase tracking-wide text-[var(--color-muted)]">Managed for you</p>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {statusOnly.map((item) => (
                    <CatalogCard key={item.serviceId} item={item}>
                      <div className="space-y-1.5">
                        {item.credentialReady ? (
                          <div>
                            <PillButton tone="amber" onClick={() => openReveal(item)}>
                              View sign-in details
                            </PillButton>
                            {item.credentialExpiresAt ? (
                              <p className="mt-1 text-[11px] text-[var(--color-muted)]">
                                One-time reveal, expires {item.credentialExpiresAt.slice(0, 10)}
                              </p>
                            ) : null}
                          </div>
                        ) : null}
                        <span className="inline-flex items-center gap-1.5 text-xs text-[var(--tint-green-text)]">
                          <span className="h-2 w-2 rounded-full bg-green-600" /> Active
                        </span>
                        {item.manualAccessMethod === 'instructions' && item.accessInstructions ? (
                          <>
                            <button
                              type="button"
                              onClick={() => setOpenInfo(openInfo === `signin-${item.serviceId}` ? null : `signin-${item.serviceId}`)}
                              className="block text-xs text-[var(--color-muted)] underline hover:no-underline"
                            >
                              {openInfo === `signin-${item.serviceId}` ? 'Hide sign-in help' : 'How to sign in'}
                            </button>
                            {openInfo === `signin-${item.serviceId}` ? (
                              <p className="text-xs text-[var(--color-muted)] whitespace-pre-wrap">{item.accessInstructions}</p>
                            ) : null}
                          </>
                        ) : null}
                        <button
                          type="button"
                          onClick={() => setOpenInfo(openInfo === item.serviceId ? null : item.serviceId)}
                          className="block text-xs text-[var(--color-muted)] underline hover:no-underline"
                        >
                          {openInfo === item.serviceId ? 'Hide details' : 'Details'}
                        </button>
                        {openInfo === item.serviceId ? (
                          <p className="text-xs text-[var(--color-muted)]">
                            {item.name}: Active{brandName ? `. Managed by ${brandName}.` : '.'} This runs in the
                            background; there’s nothing to open.
                          </p>
                        ) : null}
                      </div>
                    </CatalogCard>
                  ))}
                </div>
              </div>
            ) : null}

            {paused.length > 0 ? (
              <div className="space-y-3">
                <p className="text-xs uppercase tracking-wide text-[var(--color-muted)]">Paused</p>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {paused.map((item) => (
                    <CatalogCard key={item.serviceId} item={item} muted>
                      <SuspendedInfo reason={item.suspendReason} payInvoiceIds={item.payInvoiceIds} />
                    </CatalogCard>
                  ))}
                </div>
              </div>
            ) : null}
          </section>

          {/* ── Packages (§5.9) ─────────────────────────────────────────── */}
          {hasPackages ? (
            <section className="space-y-4">
              <h2 className="font-display text-lg">Packages</h2>

              {heldPackages.length > 0 ? (
                <div className="space-y-3">
                  <p className="text-xs uppercase tracking-wide text-[var(--color-muted)]">Your packages</p>
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {heldPackages.map((pkg) => {
                      const key = pkg.clientPackageId ?? pkg.packageId;
                      return (
                        <PackageCard
                          key={key}
                          pkg={pkg}
                          open={openPkg === key}
                          onToggle={() => setOpenPkg(openPkg === key ? null : key)}
                        />
                      );
                    })}
                  </div>
                </div>
              ) : null}

              {availablePackages.length > 0 ? (
                <div className="space-y-3">
                  <p className="text-xs uppercase tracking-wide text-[var(--color-muted)]">Packages you can add</p>
                  {blocked ? (
                    <p className="text-sm text-[var(--tint-red-text)]">Pay your past-due balance to add packages.</p>
                  ) : null}
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {availablePackages.map((pkg) => (
                      <PackageCard key={pkg.packageId} pkg={pkg} addable={!blocked} onAdd={() => openAddPkg(pkg)} />
                    ))}
                  </div>
                </div>
              ) : null}
            </section>
          ) : null}

          {/* ── Add services ────────────────────────────────────────────── */}
          {!managed ? (
          <section className="space-y-4">
            <h2 className="font-display text-lg">Add services</h2>
            {blocked ? (
              <p className="text-sm text-[var(--tint-red-text)]">
                You have a past-due balance. Pay it from Billing to add new services.
              </p>
            ) : null}

            {available.length === 0 ? (
              <Card>
                <p className="text-sm text-[var(--color-muted)]">
                  You’re all set; there’s nothing else to add right now.
                </p>
              </Card>
            ) : null}

            {recommended.length > 0 ? (
              <div className="space-y-3">
                <p className="text-xs uppercase tracking-wide text-[var(--color-muted)]">Recommended for you</p>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {recommended.map((item) => (
                    <CatalogCard key={item.serviceId} item={item} recommended>
                      <Button type="button" variant="secondary" size="sm" disabled={blocked} onClick={() => openAdd(item)}>
                        Add
                      </Button>
                    </CatalogCard>
                  ))}
                </div>
              </div>
            ) : null}

            {more.length > 0 ? (
              <div className="space-y-3">
                {recommended.length > 0 ? (
                  <p className="text-xs uppercase tracking-wide text-[var(--color-muted)]">More services</p>
                ) : null}
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {more.map((item) => (
                    <CatalogCard key={item.serviceId} item={item}>
                      <Button type="button" variant="secondary" size="sm" disabled={blocked} onClick={() => openAdd(item)}>
                        Add
                      </Button>
                    </CatalogCard>
                  ))}
                </div>
              </div>
            ) : null}
          </section>
          ) : null}
        </>
      ) : null}

      {/* ── Recurring-charge consent (§5.5) ───────────────────────────── */}
      <ConfirmDialog
        open={addTarget !== null}
        title={addTarget ? `Add ${addTarget.name}?` : 'Add service'}
        confirmLabel="Add service"
        cancelLabel="Cancel"
        confirmVariant="primary"
        busy={addBusy}
        confirmDisabled={!acknowledged}
        onConfirm={() => void confirmAdd()}
        onCancel={() => {
          if (!addBusy) setAddTarget(null);
        }}
      >
        {addTarget ? (
          <div className="space-y-3">
            <p>
              {addTarget.metered
                ? 'This service is billed based on your usage.'
                : addTarget.price && addTarget.price > 0
                  ? `This service is $${addTarget.price.toFixed(2)} per month.`
                  : 'This service is included at no additional charge.'}{' '}
              It’s a recurring subscription that continues until you ask us to cancel
              {supportEmail ? `, just email ${supportEmail}` : ''}.
            </p>
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
              />
              <span>I understand this enrolls me in a recurring monthly charge that continues until I cancel.</span>
            </label>
            {addError ? <p className="text-sm text-[var(--tint-red-text)]">{addError}</p> : null}
          </div>
        ) : null}
      </ConfirmDialog>

      {/* ── Package recurring-charge consent (§5.5/§5.9) ───────────────── */}
      <ConfirmDialog
        open={addPkgTarget !== null}
        title={addPkgTarget ? `Add ${addPkgTarget.name}?` : 'Add package'}
        confirmLabel="Add package"
        cancelLabel="Cancel"
        confirmVariant="primary"
        busy={addPkgBusy}
        confirmDisabled={!pkgAck}
        onConfirm={() => void confirmAddPkg()}
        onCancel={() => {
          if (!addPkgBusy) setAddPkgTarget(null);
        }}
      >
        {addPkgTarget ? (
          <div className="space-y-3">
            <p>
              {addPkgTarget.price > 0
                ? `This package is $${addPkgTarget.price.toFixed(2)} per month`
                : 'This package is included at no additional charge'}
              {addPkgTarget.contents.length > 0
                ? `, and includes ${addPkgTarget.contents.length} service${addPkgTarget.contents.length === 1 ? '' : 's'}: ${addPkgTarget.contents.map((c) => c.name).join(', ')}.`
                : '.'}{' '}
              It’s a recurring subscription that continues until you ask us to cancel
              {supportEmail ? `, just email ${supportEmail}` : ''}.
            </p>
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={pkgAck}
                onChange={(e) => setPkgAck(e.target.checked)}
              />
              <span>I understand this enrolls me in a recurring monthly charge that continues until I cancel.</span>
            </label>
            {addPkgError ? <p className="text-sm text-[var(--tint-red-text)]">{addPkgError}</p> : null}
          </div>
        ) : null}
      </ConfirmDialog>

      {/* ── One-time credential reveal (08 Q32): warn, then show once ──── */}
      <ConfirmDialog
        open={revealTarget !== null}
        title="View your sign-in details?"
        confirmLabel="Show them now"
        cancelLabel="Not yet"
        confirmVariant="primary"
        busy={revealBusy}
        onConfirm={() => void doReveal()}
        onCancel={() => {
          if (!revealBusy) setRevealTarget(null);
        }}
      >
        <p>
          For your security these can be shown only once
          {revealTarget?.credentialExpiresAt ? `, and expire on ${revealTarget.credentialExpiresAt.slice(0, 10)}` : ''}.
          Have a password manager or a safe place ready before you continue.
        </p>
        {revealError ? <p className="text-sm text-[var(--tint-red-text)]">{revealError}</p> : null}
      </ConfirmDialog>

      <ConfirmDialog
        open={revealed !== null}
        title="Your sign-in details"
        confirmLabel="Done"
        cancelLabel="Close"
        confirmVariant="primary"
        onConfirm={closeRevealed}
        onCancel={closeRevealed}
      >
        {revealed ? (
          <div className="space-y-3">
            <div className="rounded-md border border-[rgb(var(--edge-rgb)/0.10)] bg-[rgb(var(--edge-rgb)/0.03)] p-3 font-mono text-sm space-y-2">
              <div className="flex items-center justify-between gap-2">
                <span className="break-all">{revealed.username}</span>
                <PillButton size="xs" className="font-sans" onClick={() => copyCred('user', revealed.username)}>
                  {copied === 'user' ? 'Copied' : 'Copy'}
                </PillButton>
              </div>
              <div className="flex items-center justify-between gap-2">
                <span className="break-all">{revealed.password}</span>
                <PillButton size="xs" className="font-sans" onClick={() => copyCred('pass', revealed.password)}>
                  {copied === 'pass' ? 'Copied' : 'Copy'}
                </PillButton>
              </div>
            </div>
            {revealed.accessInstructions ? (
              <p className="text-xs text-[var(--color-muted)] whitespace-pre-wrap">{revealed.accessInstructions}</p>
            ) : null}
            <p className="text-sm font-medium">Sign in now and change this password right away.</p>
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={attestChecked}
                onChange={(e) => setAttestChecked(e.target.checked)}
              />
              <span>I signed in and changed the password</span>
            </label>
          </div>
        ) : null}
      </ConfirmDialog>
    </div>
  );
}

function CatalogCard({
  item,
  children,
  recommended,
  muted,
}: {
  item: ClientCatalogItem;
  children: ReactNode;
  recommended?: boolean;
  muted?: boolean;
}): JSX.Element {
  const label = priceLabel(item);
  return (
    <Card className={['flex flex-col gap-3', muted ? 'opacity-70' : ''].join(' ')}>
      <div className="flex items-start gap-3">
        <AvatarImage src={item.logoUrl || null} name={item.name} size={40} shape="square" alt={`${item.name} logo`} />
        <div className="min-w-0 flex-1">
          <p className="text-[11px] uppercase tracking-wide text-[var(--color-muted)]">{item.category}</p>
          <h3 className="font-display text-base leading-snug">{item.name}</h3>
        </div>
        {recommended ? (
          <span className="shrink-0 rounded-full bg-[var(--color-accent)]/25 text-[var(--ink-brand)] px-2 py-0.5 text-[11px] font-medium">
            Suggested
          </span>
        ) : null}
      </div>

      {item.description ? (
        <p className="text-sm text-[var(--color-muted)] line-clamp-2">{item.description}</p>
      ) : null}

      <div className="mt-auto flex items-center justify-between gap-2 pt-1">
        {label ? <span className="text-sm font-medium text-[var(--color-text)]">{label}</span> : <span />}
        {children}
      </div>
    </Card>
  );
}

function PackageCard({
  pkg,
  open,
  onToggle,
  addable,
  onAdd,
}: {
  pkg: ClientCatalogPackage;
  open?: boolean;
  onToggle?: () => void;
  addable?: boolean;
  onAdd?: () => void;
}): JSX.Element {
  const label = packagePriceLabel(pkg);
  const suspended = pkg.status === 'suspended';
  const count = pkg.contents.length;
  const countLabel = `${count} service${count === 1 ? '' : 's'}`;
  return (
    <Card className={['flex flex-col gap-3', suspended ? 'opacity-70' : ''].join(' ')}>
      <div className="flex items-start gap-3">
        <AvatarImage src={pkg.logoUrl || null} name={pkg.name} size={40} shape="square" alt={`${pkg.name} logo`} />
        <div className="min-w-0 flex-1">
          <p className="text-[11px] uppercase tracking-wide text-[var(--color-muted)]">
            {pkg.category ? `${pkg.category} · ${countLabel}` : countLabel}
          </p>
          <h3 className="font-display text-base leading-snug">{pkg.name}</h3>
        </div>
        <span className="shrink-0 rounded-full bg-[var(--color-accent)]/25 text-[var(--ink-brand)] px-2 py-0.5 text-[11px] font-medium">
          Package
        </span>
      </div>

      {pkg.description ? (
        <p className="text-sm text-[var(--color-muted)] line-clamp-2">{pkg.description}</p>
      ) : null}

      {suspended ? <SuspendedInfo reason={pkg.suspendReason} payInvoiceIds={pkg.payInvoiceIds} /> : null}

      {/* Available: show what's inside so the client can decide before adding (§5.9 "view and add"). */}
      {addable && count > 0 ? (
        <p className="text-xs text-[var(--color-muted)]">Includes: {pkg.contents.map((c) => c.name).join(', ')}</p>
      ) : null}

      <div className="mt-auto flex items-center justify-between gap-2 pt-1">
        {label ? <span className="text-sm font-medium text-[var(--color-text)]">{label}</span> : <span />}
        {addable ? (
          <Button type="button" variant="secondary" size="sm" onClick={onAdd}>
            Add
          </Button>
        ) : (
          <button
            type="button"
            onClick={onToggle}
            className="text-xs text-[var(--color-muted)] underline hover:no-underline"
          >
            {open ? 'Hide services' : `View ${countLabel}`}
          </button>
        )}
      </div>

      {/* Held + open: a package isn't a single launcher (§5.9) — open it to launch each contained
          service. Launchable members get an Open link; status-only members show as managed. */}
      {!addable && open && count > 0 ? (
        <ul className="space-y-2 border-t border-[rgb(var(--edge-rgb)/0.10)] pt-3">
          {pkg.contents.map((c) => (
            <li key={c.serviceId} className="flex items-center gap-2">
              <AvatarImage src={c.logoUrl || null} name={c.name} size={28} shape="square" alt={`${c.name} logo`} />
              <span className="min-w-0 flex-1 truncate text-sm">{c.name}</span>
              {c.launchUrl ? (
                <a
                  href={c.launchUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                  onClick={() => void api.account.logServiceOpen(c.serviceId)}
                  className={buttonClasses('primary', 'sm')}
                >
                  Open ↗
                </a>
              ) : (
                <span className="inline-flex items-center gap-1.5 text-xs text-[var(--tint-green-text)]">
                  <span className="h-2 w-2 rounded-full bg-green-600" /> Active
                </span>
              )}
            </li>
          ))}
        </ul>
      ) : null}
    </Card>
  );
}
